import React, { useState, useEffect } from "react";
import { Parallax } from "react-scroll-parallax";

const Services = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getSpeed = (desktopSpeed) =>
    isMobile ? desktopSpeed * 0.3 : desktopSpeed;

  const services = [
    {
      number: "1",
      title: "Artificial Intelligence",
      image: "/images/services/ai.svg",
      tags: ["AI"],
    },
    {
      number: "2",
      title: "Augmented, Virtual & Mixed Reality",
      image: "/images/services/ar-vr.svg",
      tags: ["AR", "VR", "MR"],
    },
    {
      number: "3",
      title: "Enterprise Tech Solutions",
      image: "/images/services/enterprise.svg",
      tags: ["SOFTWARE", "CLOUD", "DEVOPS", "CYBER SECURITY", "CONSULTING"],
    },
  ];

  const parallaxConfigs = [
    { speed: -10, translateY: [20, -20] },
    { speed: 5, translateY: [-15, 15] },
    { speed: -15, translateY: [25, -25] },
  ];

  return (
    <section
      className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 overflow-hidden scroll-mt-0"
      style={{ scrollBehavior: "smooth" }}
    >
      {/* Background Floating Elements */}
      <Parallax
        speed={getSpeed(-25)}
        className="absolute pointer-events-none"
        style={{ left: "-10%", top: "20%" }}
      >
        <div
          style={{
            width: "clamp(200px, 40vw, 400px)",
            height: "clamp(200px, 40vw, 400px)",
            background:
              "radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(80px)",
          }}
        ></div>
      </Parallax>

      <Parallax
        speed={getSpeed(20)}
        className="absolute pointer-events-none"
        style={{ right: "-5%", top: "50%" }}
      >
        <div
          style={{
            width: "clamp(180px, 35vw, 350px)",
            height: "clamp(180px, 35vw, 350px)",
            background:
              "radial-gradient(circle, rgba(20, 184, 166, 0.12) 0%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(70px)",
          }}
        ></div>
      </Parallax>

      <div className="relative z-10 max-w-7xl mx-auto w-full">
        {/* Section Title */}

        <Parallax
          speed={getSpeed(-12)}
          translateY={isMobile ? [10, -10] : [25, -25]}
        >
          <h2
            className="font-normal mb-8 sm:mb-12 md:mb-16 bg-gradient-to-r from-[#009999] via-white to-white bg-clip-text text-transparent"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "clamp(32px, 5vw, 64px)",
              lineHeight: "1.2",
              letterSpacing: "0.08em",
            }}
          >
            Our Services
          </h2>
        </Parallax>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <Parallax
              key={index}
              speed={getSpeed(parallaxConfigs[index].speed)}
              translateY={
                isMobile ? [5, -5] : parallaxConfigs[index].translateY
              }
              disabled={isMobile}
            >
              <div className="relative group w-full">
                {/* Service Card */}
                <div className="relative overflow-hidden rounded-2xl">
                  {/* Service Image */}
                  <div
                    className="relative w-full"
                    style={{
                      height: "clamp(200px, 35vw, 280px)",
                      background:
                        "linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(20, 184, 166, 0.05) 100%)",
                      borderRadius: "16px",
                    }}
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-contain p-6 sm:p-8"
                    />
                  </div>

                  {/* Service Info */}
                  <div className="mt-4 sm:mt-6 px-4 pb-4">
                    <h3
                      className="font-medium mb-3 sm:mb-4"
                      style={{
                        fontFamily: "Montserrat, sans-serif",
                        fontSize: "clamp(16px, 2vw, 24px)",
                        lineHeight: "1.3",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {service.title}
                    </h3>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2">
                      {service.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-2 sm:px-3 py-1 rounded-full"
                          style={{
                            background: "rgba(6, 182, 212, 0.15)",
                            border: "1px solid rgba(6, 182, 212, 0.3)",
                            fontFamily: "Montserrat, sans-serif",
                            fontSize: "clamp(10px, 1.5vw, 12px)",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Parallax>
          ))}
        </div>

        {/* CTA Button */}
        <Parallax speed={getSpeed(-4)} disabled={isMobile}>
          <button
            onClick={() => {
              const footer = document.querySelector("footer");
              footer?.scrollIntoView({ behavior: "smooth" });
            }}
            className="bg-white text-black rounded-full font-medium hover:bg-gray-100 transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mx-auto block flex items-center justify-center gap-2"
            style={{
              width: "clamp(14rem, 25vw, 18rem)",
              minHeight: "clamp(2.5rem, 5.5vh, 3.375rem)",
              fontSize: "clamp(0.875rem, 1.8vw, 1rem)",
              borderRadius: "3.125rem",
              fontFamily: "Montserrat, sans-serif",
              letterSpacing: "0.02em",
            }}
          >
            Let’s Work Together →
          </button>
        </Parallax>
      </div>
    </section>
  );
};

export default Services;
